/**
 * Custom hooks for OKR check-ins
 * React Query hooks for data fetching and mutations
 */

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { checkInService } from '../services/checkin.service';
import { toast } from 'react-hot-toast';

// Type for API error responses
interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

import type {
  CreateCheckInRequest,
  UpdateCheckInScheduleRequest,
  CheckInListResponse,
  CheckIn,
  CheckInSchedule,
  PendingCheckInsResponse,
  CheckInHistoryResponse,
  CheckInHistoryFilters,
  CheckInInsights,
  CalendarDataResponse
} from '../types/okr';

/**
 * Hook to fetch check-ins for an OKR with pagination
 */
export const useCheckIns = (
  okrId: string,
  options?: { page?: number; limit?: number }
) => {
  return useQuery<CheckInListResponse>({
    queryKey: ['checkIns', okrId, options],
    queryFn: () => checkInService.getCheckIns(okrId, options),
    enabled: !!okrId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch a single check-in by ID
 */
export const useCheckIn = (checkInId: string) => {
  return useQuery<CheckIn>({
    queryKey: ['checkIn', checkInId],
    queryFn: () => checkInService.getCheckInById(checkInId),
    enabled: !!checkInId,
  });
};

/**
 * Hook to fetch check-in schedule for an OKR
 */
export const useCheckInSchedule = (okrId: string) => {
  return useQuery<CheckInSchedule>({
    queryKey: ['checkInSchedule', okrId],
    queryFn: () => checkInService.getCheckInSchedule(okrId),
    enabled: !!okrId,
  });
};

/**
 * Hook to create a new check-in
 */
export const useCreateCheckIn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ okrId, data }: { okrId: string; data: CreateCheckInRequest }) =>
      checkInService.createCheckIn(okrId, data),
    onSuccess: (_, variables) => {
      // Invalidate check-ins list for this OKR
      queryClient.invalidateQueries({ queryKey: ['checkIns', variables.okrId] });
      // Invalidate OKR data (status, schedule might have changed)
      queryClient.invalidateQueries({ queryKey: ['okr', variables.okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate check-in schedule
      queryClient.invalidateQueries({ queryKey: ['checkInSchedule', variables.okrId] });
      // Invalidate check-in history (all filters)
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'history'] });
      // Invalidate pending check-ins
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'team', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'org', 'pending'] });
      // Invalidate calendar data
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'calendar'] });
      // Invalidate org-wide queries
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'org', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'org', 'insights'] });
      toast.success('Check-in submitted successfully');
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.error || 'Failed to submit check-in';
      toast.error(message);
    }
  });
};

/**
 * Hook to update check-in schedule for an OKR
 */
export const useUpdateCheckInSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ okrId, data }: { okrId: string; data: UpdateCheckInScheduleRequest }) =>
      checkInService.updateCheckInSchedule(okrId, data),
    onSuccess: (_, variables) => {
      // Invalidate schedule
      queryClient.invalidateQueries({ queryKey: ['checkInSchedule', variables.okrId] });
      // Invalidate OKR data
      queryClient.invalidateQueries({ queryKey: ['okr', variables.okrId] });
      queryClient.invalidateQueries({ queryKey: ['okrs'] });
      // Invalidate calendar data (nextCheckInDue has changed)
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'calendar'] });
      // Invalidate pending check-ins (due dates have changed)
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['checkIns', 'team', 'pending'] });
      toast.success('Check-in schedule updated');
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.error || 'Failed to update schedule';
      toast.error(message);
    }
  });
};

// ============================================
// CHECK-IN PAGE HOOKS
// ============================================

/**
 * Hook to fetch pending check-ins for current user
 */
export const usePendingCheckIns = () => {
  return useQuery<PendingCheckInsResponse>({
    queryKey: ['checkIns', 'pending'],
    queryFn: () => checkInService.getPendingCheckIns(),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch team's pending check-ins (for managers)
 */
export const useTeamPendingCheckIns = () => {
  return useQuery<PendingCheckInsResponse>({
    queryKey: ['checkIns', 'team', 'pending'],
    queryFn: () => checkInService.getTeamPendingCheckIns(),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch check-in history across all user's OKRs
 */
export const useCheckInHistory = (filters?: CheckInHistoryFilters) => {
  return useQuery<CheckInHistoryResponse>({
    queryKey: ['checkIns', 'history', filters],
    queryFn: () => checkInService.getCheckInHistory(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch check-in insights for manager's team
 */
export const useCheckInInsights = (filters?: { cycleId?: string; startDate?: string; endDate?: string }) => {
  return useQuery<CheckInInsights>({
    queryKey: ['checkIns', 'insights', filters],
    queryFn: () => checkInService.getCheckInInsights(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - analytics don't change often
  });
};

/**
 * Hook to fetch calendar data for check-ins
 */
export const useCalendarData = (startDate: string, endDate: string) => {
  return useQuery<CalendarDataResponse>({
    queryKey: ['checkIns', 'calendar', startDate, endDate],
    queryFn: () => checkInService.getCalendarData(startDate, endDate),
    enabled: !!startDate && !!endDate,
    staleTime: 60 * 1000, // 1 minute
    // Keep the last range while the next one loads, so the calendar (and its
    // Previous/Next buttons) stays mounted. Without it, every new month was a
    // fresh `isLoading`, the tab swapped itself for a spinner, and clicks made
    // during the fetch landed on the spinner and were lost. Callers must not
    // present the kept data as the new range's: check `isPlaceholderData`.
    placeholderData: keepPreviousData,
  });
};

// ============================================
// ORG-WIDE HOOKS (ACCOUNT ADMIN ONLY)
// ============================================

/**
 * Hook to fetch org-wide pending check-ins (for account admins)
 */
export const useOrgPendingCheckIns = () => {
  return useQuery<PendingCheckInsResponse>({
    queryKey: ['checkIns', 'org', 'pending'],
    queryFn: () => checkInService.getOrgPendingCheckIns(),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch org-wide check-in history (for account admins)
 */
export const useOrgCheckInHistory = (filters?: CheckInHistoryFilters) => {
  return useQuery<CheckInHistoryResponse>({
    queryKey: ['checkIns', 'org', 'history', filters],
    queryFn: () => checkInService.getOrgCheckInHistory(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch org-wide check-in insights (for account admins)
 */
export const useOrgCheckInInsights = (filters?: { cycleId?: string; startDate?: string; endDate?: string }) => {
  return useQuery<CheckInInsights>({
    queryKey: ['checkIns', 'org', 'insights', filters],
    queryFn: () => checkInService.getOrgCheckInInsights(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - analytics don't change often
  });
};
